import { useCallback, useEffect, useRef, useState } from 'react';
import { callApi } from './managementApi';

const MAX_CONSECUTIVE_FAILURES = 10;

const initialState = { status: 'idle', phase: null, current: 0, total: 0, imported: 0, skipped: 0, checked: 0, errors: [], error: null, startedAt: null };

// Runs the article sync from the client, one article at a time, so that progress can be shown and no request ever times out.
// status is one of: idle, running, succeeded, failed, cancelled
const useArticleSync = () => {
    const [state, setState] = useState(initialState);
    const stopped = useRef(false);
    const unmounted = useRef(false);

    useEffect(() => {
        unmounted.current = false;
        return () => {
            unmounted.current = true;
        };
    }, []);

    const update = useCallback((changes) => {
        if (!unmounted.current) setState((prev) => ({ ...prev, ...changes }));
    }, []);

    const stop = useCallback(() => {
        stopped.current = true;
    }, []);

    // Calls the endpoint for every article number and reports progress. Resolves with false if the run should be aborted.
    const processAll = useCallback(async (phase, articleNumbers, endpoint, counterName, extraBody = {}) => {
        update({ phase, current: 0, total: articleNumbers.length });

        let consecutiveFailures = 0;
        for (let index = 0; index < articleNumbers.length; index++) {
            if (stopped.current || unmounted.current) return false;

            const articleNumber = articleNumbers[index];
            try {
                const result = await callApi(endpoint, { articleNumber, ...extraBody });
                consecutiveFailures = 0;
                const counter = result?.skipped ? 'skipped' : counterName;
                setState((prev) => ({ ...prev, current: index + 1, [counter]: prev[counter] + 1 }));
            } catch (err) {
                consecutiveFailures += 1;
                setState((prev) => ({ ...prev, current: index + 1, errors: [...prev.errors, { articleNumber, error: err.message }] }));

                // Stop instead of failing every remaining article when the server is unreachable
                if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
                    throw new Error(`Avbröt efter ${MAX_CONSECUTIVE_FAILURES} misslyckade anrop i rad: ${err.message}`);
                }
            }
        }
        return true;
    }, []);

    const run = useCallback(
        async (daysBack, skipSyncedWithinHours) => {
            stopped.current = false;
            setState({ ...initialState, status: 'running', phase: 'listing', startedAt: Date.now() });

            try {
                const query = daysBack ? `?daysBack=${encodeURIComponent(daysBack)}` : '';
                const { articleNumbers } = await callApi(`article-sync/active${query}`);
                const extraBody = skipSyncedWithinHours ? { skipSyncedWithinHours } : {};
                if (!(await processAll('importing', articleNumbers, 'article-sync/import', 'imported', extraBody))) {
                    update({ status: 'cancelled' });
                    return;
                }

                update({ phase: 'listingStale', current: 0, total: 0 });
                const stale = await callApi('article-sync/stale');
                if (!(await processAll('stale', stale.articleNumbers, 'article-sync/import', 'checked'))) {
                    update({ status: 'cancelled' });
                    return;
                }

                update({ status: 'succeeded', phase: null });
            } catch (err) {
                update({ status: 'failed', phase: null, error: err.message });
            }
        },
        [processAll, update]
    );

    return { ...state, running: state.status === 'running', run, stop };
};

export default useArticleSync;
